import type { AxiosError } from "axios"
import axios from "axios"
import { routeTree } from "@/app/router/route-tree"
import { getApiErrorCode, getApiErrorMessage, getApiErrorRequestId } from "@/shared/api/errors"
import { API_BASE_URL } from "@/shared/config/env"

/**
 * Paths where a 401 is part of the conversation rather than a lost session: the login form
 * reports bad credentials itself, and `me` is the request that finds out whether anybody is
 * logged in at all.
 */
const AUTH_PATHS = ["/auth/login", "/auth/logout", "/auth/me"]

const axiosInstance = axios.create({
  baseURL: API_BASE_URL,
  // The session travels in an httpOnly cookie, so no request may drop credentials.
  withCredentials: true,
})

/**
 * A failure the interface cannot explain to the visitor is a fault of the service, and the
 * screen says exactly that and no more. The console is what makes it debuggable: the
 * request id here is the one the backend logged the real reason under.
 *
 * A failure that came with a reason code is left alone — the interface has its own text for
 * it — and so is a 401 of the login form, which is an answer rather than a fault.
 */
const reportSystemError = (error: AxiosError, isAuthRequest: boolean) => {
  if (getApiErrorCode(error) || (isAuthRequest && error.response?.status === 401)) {
    return
  }

  console.error("Request failed", {
    request: `${error.config?.method?.toUpperCase() ?? ""} ${error.config?.url ?? ""}`.trim(),
    status: error.response?.status,
    requestId: getApiErrorRequestId(error),
    message: getApiErrorMessage(error) ?? error.message,
  })
}

/**
 * There is no refresh token, so an expired cookie leaves one move: ask for the password
 * again. A full page load rather than a navigation — nothing cached in this tab belongs to
 * the visitor anymore.
 */
axiosInstance.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    const requestUrl = error.config?.url ?? ""
    const isAuthRequest = AUTH_PATHS.some((path) => requestUrl.includes(path))
    const authPath = routeTree.auth.path()

    if (error.response?.status === 401 && !isAuthRequest && window.location.pathname !== authPath) {
      window.location.assign(authPath)
    }

    reportSystemError(error, isAuthRequest)

    return Promise.reject(error)
  }
)

export default axiosInstance
